import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { PrimaryPopover } from "../..";
import { ButtonTypes } from "../../../Definitions";
import { getBorderRadius } from "../../_Components";
import { useDeviceInfo } from "../../../Services";
import { useModalContext } from "../../LocationFeeder/Modals/_Modals/newSectionModalContext";
import { Station } from "../../../Definitions/newSectionModalInterfaces";
import NewButton from "../../BaseComponents/newButton";

const NewEsfWfSectionModalLvl3: React.FC = () => {
  const { state: { farm, domain, connection, error, section }, dispatch } = useModalContext()
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const [, setIsOpen] = useState<boolean>(false)
  const [isExpanded, setExpand] = useState<boolean>(false)

  const stations = { ...section, contents: section.contents as Station[] };

  const { data: deviceInfoState } = useDeviceInfo(connection);

  // Get devices to find i/o name
  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  }, [deviceInfoState, domain])

  // #region | BorderRadius
  const parseGridArea = (area: string): [number, number] => {
    const [rowStart, colStart] = area.split("/").map(str => parseInt(str, 10));
    return [rowStart - 1, colStart - 1]; // 0-indekseret
  };
  // #endregion

  // #region | Station View
  const stationView = useMemo(() => {
    const views: JSX.Element[] = [];
  
    Array.from(stations.contents).forEach((station) => {
      const [row, col] = station.visual ? parseGridArea(station.visual) : [0, 0];
      const borderRadius = !mobileXs ? getBorderRadius({ row, col, columns: section.columns, rows: section.rows }) : undefined;
  
      const stationElement: JSX.Element[] = [];
      const popOverElement: JSX.Element[] = [];
  
      const name = station.name ?? "";
      stationElement.push(
        <div key={'name_' + station.stationId} style={{whiteSpace: "nowrap", gridArea: "1/1/2/2", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })}: `} <b>{name}</b>
        </div>
      );
  
      const ioBoard = deviceInfo.find((io) => io.id === station.io)?.name ?? "";
      stationElement.push(
        <div key={'ioBoard_' + station.stationId} style={{whiteSpace: "nowrap", gridArea: "1/2/2/3", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("devices.aicoIo", { defaultValue: "I/O" })}: `} <b>{ioBoard}</b>
        </div>
      );
  
      popOverElement.push(
        <p key={'feederOutput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.feederOutput", {defaultValue: "Feeder Output"})}: ${station.feederOutput ?? ""}`}
        </p>
      );

      popOverElement.push(
        <p key={'feederInput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.feederInput", {defaultValue: "Feeder Input"})}: ${station.feederInput ?? ""}`}
        </p>
      );

      popOverElement.push(
        <p key={'gateMotorOutput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.gateMotorOutput", {defaultValue: "Gate Motor Output"})}: ${station.gateMotorOutput ?? ""}`}
        </p>
      );

      popOverElement.push(
        <p key={'gateMotorInput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.gateMotorInput", {defaultValue: "Gate Motor Input"})}: ${station.gateMotorInput ?? ""}`}
        </p>
      );

      popOverElement.push(
        <p key={'gateSensor_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.gateSensor", {defaultValue: "Gate Sensor"})}: ${station.gateSensor ?? ""}`}
        </p>
      );

      if (station.waterOutput != null)
      popOverElement.push(
        <p key={'waterOutput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.waterOutput", {defaultValue: "Water Output"})}: ${station.waterOutput ?? ""}`}
        </p>
      );

      if (station.buttonInput != null)
        popOverElement.push(
          <p key={'buttonInput_' + station.stationId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
            {`${t("locationFeederPages.sections.buttonInput", {defaultValue: "Button Input"})}: ${station.buttonInput ?? ""}`}
          </p>
        );
  
      views.push(
        <div key={'station_' + station.stationId} style={{
          gridArea: station.visual,
          backgroundColor: colors.greyLightSecondary,
          display: "grid",
          padding: "0px 10px",
          gridTemplateColumns: isExpanded ? "repeat(2, 1fr)" : "1fr 2fr 20px",
          gridTemplateRows: isExpanded ? "30px 1fr" : "30px",
          gap: "0px 10px",
          borderRadius
        }}>
          {stationElement}
          <div style={{ gridArea: isExpanded ? "2/1/3/3" : "1/3/2/4", display: "flex", alignItems: "center", borderTop: isExpanded ? "2px solid "+colors.greyTertiary : "none" }}>
            {isExpanded ? <div style={{display: "flex", flexDirection: "column", gap: "5px", padding: "10px 0px"}}>{popOverElement}</div>
            : (<PrimaryPopover
                content={
                  <div onClick={() => setIsOpen(true)} style={{ cursor: "pointer", display: "flex" }}>
                    <glyphs.Note fillColor={colors.greyDarkPrimary} />
                  </div>
                }
                overrides={{ popoverBgColor: colors.blackPrimary }}
                popoverHeader={`${t("devices.ioSpecifications", {defaultValue: "i/o specifications"})}`}
                popoverContent={
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {popOverElement}
                  </div>
                }
              />)
            }
          </div>
        </div>
      );
    });
  
    return views;
  }, [stations.contents, section.columns, section.rows, mobileXs, t, deviceInfo, isExpanded]);
  // #endregion

  return ( <>
    <div style={{position: "absolute", top: "185px", right: "45px"}}>
      <NewButton
        content={isExpanded ? t("locationFeederPages.sections.collapse", {defaultValue: "collapse"}) : t("locationFeederPages.sections.expand", {defaultValue: "expand"})}
        buttonType={ButtonTypes.textButton}
        endEnhancer={glyphs.Arrow({rotate: isExpanded ? 90 : -90, size: "15px", transparency: isExpanded ? 1 : 0.3 })}
        onClick={() => setExpand(!isExpanded)}
      />
    </div>
    <div style={{
      display: !mobileXs ? "grid" : "flex",
      flexDirection: "column",
      gridTemplateRows: !mobileXs ? `repeat(${section.rows}, 1fr)` : "none",
      gridTemplateColumns: !mobileXs ? `repeat(${section.columns}, 1fr)` : "none",
      gap: "10px",
      borderRadius: "10px",
      overflow: "hidden"
    }}>
        {stationView}
      </div>
  </> );
}

export default NewEsfWfSectionModalLvl3